import React from "react";
import { Row, Col } from "reactstrap";
import { connect } from "react-redux";
import { css } from "@emotion/css";

import { changePokemon } from "../../actions";
import { IState } from "../../reducers";

interface IDescriptionProps {
  name: string;
  types: string[];
  height: string;
  weight: string;
  abilities: { normal: string[]; hidden: string[] };
  family: {
    id: number;
    evolutionStage: number;
    evolutionLine: { name: string; number: string; sprite: string }[];
  };
  changePokemon: (index: string) => void;
}

const Description = (props: IDescriptionProps) => {
  const { name, types, height, weight, abilities, family, changePokemon } =
    props;

  const renderTypes = () => {
    return (
      <Row className="mb-2 mb-md-3">
        {types.map((type, index) => (
          <Col key={index} className="text-center">
            <span className={`btn btn-${type.toLowerCase()} ${typeBadgeStyle}`}>
              {type}
            </span>
          </Col>
        ))}
      </Row>
    );
  };

  const renderHeightWeight = () => {
    return (
      <Row className="text-center my-2 my-md-3">
        <Col>
          <h4 className="mb-0 mb-md-3">Height</h4>
          <div className={heightWeightValueStyle}>{height}</div>
        </Col>
        <Col>
          <h4 className="mb-0 mb-md-3">Weight</h4>
          <div className={heightWeightValueStyle}>{weight}</div>
        </Col>
      </Row>
    );
  };

  const renderAbilities = () => {
    return (
      <>
        <Row className="mt-2 mt-md-3">
          <h4 className="m-auto">Abilities</h4>
        </Row>
        <Row className="text-center mt-2 mb-3">
          <Col>
            <h5 className={abilityLabelStyle}>Normal</h5>
            <div className={abilityContainerStyle}>
              {abilities.normal.map((ability, index) => (
                <div key={index} className={abilityValueStyle}>
                  {ability}
                </div>
              ))}
              {abilities.normal.length === 0 && (
                <div className={abilityValueStyle}>-</div>
              )}
            </div>
          </Col>
          <Col>
            <h5 className={abilityLabelStyle}>Hidden</h5>
            <div className={abilityContainerStyle}>
              {abilities.hidden.map((ability, index) => (
                <div key={index} className={abilityValueStyle}>
                  {ability}
                </div>
              ))}
              {abilities.hidden.length === 0 && (
                <div className={abilityValueStyle}>-</div>
              )}
            </div>
          </Col>
        </Row>
      </>
    );
  };

  const renderEvolutionLine = (pokemonName: string) => {
    return (
      <>
        <Row className="mt-2 mt-md-3">
          <h4 className="m-auto">Evolution Line</h4>
        </Row>
        <Row className="text-center mt-3 mb-4 mb-sm-0 justify-content-center">
          {family.evolutionLine.length === 1 ? (
            <Col xs={12}>
              <div className={evolutionLineContainerStyle}>
                <p className={evolutionLineEmptyStyle}>
                  This Pokémon has no evolution line
                </p>
              </div>
            </Col>
          ) : (
            family.evolutionLine.map(({ name, number: i, sprite }, index) => (
              <Col
                className={evolutionLineContainerStyle}
                onClick={() => pokemonName !== name && changePokemon(i)}
                style={{ cursor: "pointer" }}
                key={index}
              >
                <img width="60" src={sprite} alt={name} />
                <h6 className="mt-3 mb-0">{name}</h6>
              </Col>
            ))
          )}
        </Row>
      </>
    );
  };

  return (
    <Col md="12" lg="4" className={containerStyle}>
      {renderTypes()}
      {renderHeightWeight()}
      {renderAbilities()}
      {renderEvolutionLine(name)}
    </Col>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 991px) {
    margin-top: 1.5rem;
  }

  @media (max-width: 767px) {
    margin-top: 2rem;
  }
`;

const typeBadgeStyle = css`
  cursor: default;
  pointer-events: none;
  user-select: none;

  &:hover,
  &:focus,
  &:active {
    transform: none !important;
    box-shadow: none !important;
  }
`;

const heightWeightValueStyle = css`
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 1.2;
  color: white;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const abilityLabelStyle = css`
  @media (max-width: 761px) {
    margin-bottom: 0;
  }
`;

const abilityValueStyle = css`
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
  color: white;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const evolutionLineContainerStyle = css`
  height: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const evolutionLineEmptyStyle = css`
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.2;
  align-self: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const abilityContainerStyle = css`
  height: 50px;
`;

const mapStateToProps = (state: IState) => {
  const { pokemons, currentPokemon, currentForm } = state.pokemon;
  const { name, types, height, weight, abilities, family } =
    pokemons[currentPokemon][currentForm];

  return { name, types, height, weight, abilities, family };
};

export default connect(mapStateToProps, { changePokemon })(Description);
